// @ts-check
/** A small synthesized event mix. Audio begins only after an explicit gesture;
 * all notes stop on pause/disposal. No network audio assets or parallel game clock.
 */
export function createAudio() {
  /** @type {AudioContext|null} */ let context = null;
  let enabled = false;
  let beatTime = 0, beat = 0;
  /** @type {Set<OscillatorNode>} */ const voices = new Set();
  /** @param {number} frequency @param {number} duration @param {number} [delay] */
  function tone(frequency, duration, delay = 0, volume = 0.055) {
    if (!enabled || !context || context.state !== 'running' || voices.size > 14) return;
    const oscillator = context.createOscillator(), gain = context.createGain(), start = context.currentTime + delay;
    oscillator.type = 'triangle'; oscillator.frequency.setValueAtTime(frequency, start); oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.7, start + duration);
    gain.gain.setValueAtTime(0, start); gain.gain.linearRampToValueAtTime(volume, start + 0.008); gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
    oscillator.connect(gain); gain.connect(context.destination); oscillator.start(start); oscillator.stop(start + duration + 0.02); voices.add(oscillator);
    oscillator.onended = () => { voices.delete(oscillator); oscillator.disconnect(); gain.disconnect(); };
  }
  function silence() { for (const voice of voices) { voice.stop(); voice.disconnect(); } voices.clear(); }
  return {
    get enabled() { return enabled; },
    async toggle() {
      enabled = !enabled;
      if (!enabled) { silence(); return; }
      try { if (!context) context = new window.AudioContext(); await context.resume(); tone(660, 0.12); } catch { enabled = false; }
    },
    /** @param {import('../game/types.js').GameEvent} event */
    accept(event) {
      const notes = /** @type {Partial<Record<import('../game/types.js').GameEventType,number>>} */ ({ 'transistor-found': 1175, jump: 330, 'double-jump': 520, land: 110, 'block-bumped': 160, 'block-broken': 120, 'block-activated': 580, pickup: 880, 'enemy-stomped': 220, 'pulse-fired': 700, damaged: 130, checkpoint: 660, signal: 550, 'boss-hit': 180, 'boss-defeated': 880, 'uplink-entered': 740, failed: 90, 'time-warning': 440, 'signal-captured': 988, 'secret-entered': 1397, 'secret-returned': 698 });
      const frequency = notes[event.type];
      if (frequency) tone(frequency, event.type === 'failed' ? 0.55 : 0.14);
      if (['checkpoint', 'boss-defeated', 'uplink-entered', 'signal-captured', 'secret-entered'].includes(event.type)) { tone(880, 0.16, 0.1); tone(1100, 0.25, 0.22); }
      if (event.type === 'reward-spawned' && event.reward === 'bit') { tone(988, .1); tone(1318, .12, .08); }
      if (event.type === 'scored' && (event.chain ?? 0) > 1) tone(440 * 2 ** ((event.chain ?? 1) / 12), .12);
      if (event.type === 'restarted') beat = beatTime = 0;
    },
    /** Quiet procedural pulse follows the simulation, with a distinct register per world.
     * @param {number} dt @param {number} world @param {boolean} boss
     */
    step(dt, world, boss) {
      if (!enabled) return;
      beatTime += dt;
      const duration = boss ? .22 : .34;
      if (beatTime < duration) return;
      beatTime %= duration;
      const root = [110, 123.47, 98, 130.81, 146.83, 82.41, 103.83, 73.42, 174.61][world - 1] ?? 110;
      const sequence = [0, 7, 12, 7, 3, 10, 15, 10, 5, 12, 17, 12, 3, 10, 7, 10];
      tone(root * 2 ** (sequence[beat % sequence.length] / 12), .25, 0, .009);
      if (beat % 4 === 0) tone(root / 2, .38, 0, .018);
      beat++;
    },
    pause: silence,
    dispose() { enabled = false; silence(); if (context) void context.close().catch(() => {}); context = null; },
  };
}
